<template>
  <div>
    <!-- Filter-Leiste -->
    <v-card class="mb-4" outlined>
      <v-card-title class="py-2">
        <v-icon left>mdi-monitor-eye</v-icon>
        Preisdaten-Monitor
        <v-spacer></v-spacer>
        <v-chip v-if="isSubscribed" color="success" small dark class="mr-2">
          <v-icon left x-small>mdi-broadcast</v-icon>
          Live
        </v-chip>
        <v-tooltip bottom v-if="geladeneProdukte > 0">
          <template v-slot:activator="{ on, attrs }">
            <span
              class="caption mr-2"
              :class="ladeVorgangAktiv ? 'warning--text' : 'grey--text'"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon x-small left :color="ladeVorgangAktiv ? 'warning' : 'grey'">
                {{ ladeVorgangAktiv ? "mdi-progress-download" : "mdi-database-check" }}
              </v-icon>
              {{ geladeneProdukte }} / {{ produktListe.length }} geladen
            </span>
          </template>
          <span>
            Ladefortschritt: Für {{ geladeneProdukte }} von {{ produktListe.length }} abonnierten
            Produkten liegen bereits Preisdaten vor.<br />
            <template v-if="ladeVorgangAktiv">
              {{ fehlendeProdukte }} Produkte werden noch geladen.
            </template>
            <template v-else> Alle Produkte sind geladen und werden live aktualisiert. </template>
            <br />
            Die Produktanzahl ergibt sich aus dem Filter (Energietyp/Laufzeiten) und ist unabhängig
            von den Schnellfiltern der Tabelle.
          </span>
        </v-tooltip>
        <v-btn icon @click="showConfig = !showConfig" title="Darstellung konfigurieren">
          <v-icon>mdi-cog</v-icon>
        </v-btn>
        <v-btn
          icon
          @click="ansichtsModus = ansichtsModus === 'preis' ? 'delta' : 'preis'"
          :title="ansichtsModus === 'preis' ? 'Import-Verzögerung anzeigen' : 'Preisansicht anzeigen'"
          :color="ansichtsModus === 'delta' ? 'warning' : ''"
        >
          <v-icon>{{ ansichtsModus === 'preis' ? 'mdi-clock-alert-outline' : 'mdi-currency-eur' }}</v-icon>
        </v-btn>
        <v-btn icon @click="restart" :loading="$store.state.preismonitor.loading" title="Neu laden">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-0">
        <div class="d-flex flex-wrap align-center" style="gap: 6px;">
          <!-- Energietyp -->
          <v-chip-group v-model="schnellfilter.energietyp" mandatory active-class="primary--text">
            <v-chip small filter outlined value="alle">Alle</v-chip>
            <v-chip small filter outlined value="Strom">⚡ Strom</v-chip>
            <v-chip small filter outlined value="Gas">🔥 Gas</v-chip>
          </v-chip-group>
          <v-divider vertical class="mx-1" style="height:24px;"></v-divider>
          <!-- Base/Peak -->
          <v-chip-group v-model="schnellfilter.produkttyp" mandatory active-class="primary--text">
            <v-chip small filter outlined value="alle">Alle</v-chip>
            <v-chip small filter outlined value="Base">Base</v-chip>
            <v-chip small filter outlined value="Peak">Peak</v-chip>
          </v-chip-group>
          <v-divider vertical class="mx-1" style="height:24px;"></v-divider>
          <!-- Zusätzliche Produkte -->
          <v-chip
            small
            filter
            outlined
            :input-value="zeigeQuartale"
            @click="toggleQuartale"
          >
            + Quartale
          </v-chip>
          <v-spacer></v-spacer>
          <v-chip color="info" dark small v-if="letzterImportZeit !== '—'">
            <v-icon left x-small>mdi-clock</v-icon>
            {{ letzterImportZeit }}
          </v-chip>
          <v-chip outlined small>
            {{ gefilterteZeilen.length }} Produkte
          </v-chip>
        </div>
      </v-card-text>
    </v-card>

    <!-- Konfigurations-Dialog -->
    <v-dialog v-model="showConfig" max-width="700" scrollable>
      <v-card>
        <v-card-title>Darstellung konfigurieren</v-card-title>
        <v-card-text>
          <v-subheader>Schwellwerte (% Abweichung) pro Produktkategorie</v-subheader>
          <div v-for="et in ['Strom', 'Gas']" :key="et">
            <div v-for="pt in ['Base', 'Peak']" :key="et + pt" class="mb-4">
              <div class="subtitle-2 mb-1">{{ et }} / {{ pt }}</div>
              <v-row dense>
                <v-col v-for="(s, idx) in schwellwerte[et][pt].stufen" :key="idx" cols="2">
                  <v-text-field
                    :value="s"
                    @input="updateSchwellwert(et, pt, idx, $event)"
                    :label="'Stufe ' + (idx + 1)"
                    type="number"
                    step="0.1"
                    dense
                    outlined
                    hide-details
                    suffix="%"
                  ></v-text-field>
                </v-col>
              </v-row>
            </div>
          </div>
          <v-divider class="my-4"></v-divider>
          <v-subheader>Farbwerte</v-subheader>
          <v-row dense>
            <v-col cols="6">
              <div class="subtitle-2 mb-2">Steigend (Rot-Töne)</div>
              <div v-for="(farbe, idx) in farbkonfiguration.steigend" :key="'s'+idx" class="d-flex align-center mb-1">
                <div :style="{ backgroundColor: farbe, width: '30px', height: '30px', borderRadius: '4px', marginRight: '8px', border: '1px solid #ccc' }"></div>
                <v-text-field
                  :value="farbe"
                  @input="updateFarbe('steigend', idx, $event)"
                  :label="'Stufe ' + (idx + 1)"
                  dense
                  outlined
                  hide-details
                  style="max-width: 140px"
                ></v-text-field>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="subtitle-2 mb-2">Fallend (Grün-Töne)</div>
              <div v-for="(farbe, idx) in farbkonfiguration.fallend" :key="'f'+idx" class="d-flex align-center mb-1">
                <div :style="{ backgroundColor: farbe, width: '30px', height: '30px', borderRadius: '4px', marginRight: '8px', border: '1px solid #ccc' }"></div>
                <v-text-field
                  :value="farbe"
                  @input="updateFarbe('fallend', idx, $event)"
                  :label="'Stufe ' + (idx + 1)"
                  dense
                  outlined
                  hide-details
                  style="max-width: 140px"
                ></v-text-field>
              </div>
            </v-col>
          </v-row>
          <v-divider class="my-4"></v-divider>
          <v-subheader>Import-Verzögerung (Gelb → Blau)</v-subheader>
          <div
            v-for="(stufe, idx) in deltaKonfiguration.stufen"
            :key="'delta-'+idx"
            class="d-flex align-center mb-2"
          >
            <div
              :style="{
                backgroundColor: stufe.farbe,
                width: '30px', height: '30px',
                borderRadius: '4px', marginRight: '8px',
                border: '1px solid #ccc'
              }"
            ></div>
            <v-text-field
              :value="stufe.bisMinuten"
              @input="updateDeltaStufe(idx, 'bisMinuten', $event)"
              label="bis (Min)"
              type="number"
              dense outlined hide-details
              style="max-width: 100px"
              suffix="min"
              class="mr-2"
            ></v-text-field>
            <v-text-field
              :value="stufe.farbe"
              @input="updateDeltaStufe(idx, 'farbe', $event)"
              label="Farbe"
              dense outlined hide-details
              style="max-width: 140px"
            ></v-text-field>
          </div>
          <div class="d-flex align-center mt-2">
            <div
              :style="{
                backgroundColor: deltaKonfiguration.fallback,
                width: '30px', height: '30px',
                borderRadius: '4px', marginRight: '8px',
                border: '1px solid #ccc'
              }"
            ></div>
            <span class="mr-2 caption">Darüber:</span>
            <v-text-field
              :value="deltaKonfiguration.fallback"
              @input="updateDeltaFallback($event)"
              label="Fallback-Farbe"
              dense outlined hide-details
              style="max-width: 140px"
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showConfig = false">Schließen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading -->
    <v-progress-linear v-if="$store.state.preismonitor.loading" indeterminate color="primary" class="mb-4"></v-progress-linear>

    <!-- Error -->
    <v-alert v-if="$store.state.preismonitor.error" type="error" class="mb-4" dismissible>
      {{ $store.state.preismonitor.error }}
    </v-alert>

    <!-- Heatmap Grid (untertägig) -->
    <v-card outlined v-if="gefilterteZeilen.length > 0">
      <div class="heatmap-wrapper">
        <table class="heatmap-table">
          <thead>
            <tr class="day-header-row">
              <th class="product-header sticky-col" rowspan="2">Produkt</th>
              <th
                v-for="gruppe in tagesGruppen"
                :key="'d-' + gruppe.datum"
                :colspan="gruppe.spalten.length"
                class="day-group-header"
                :class="{ 'weekend-header': gruppe.istWochenende }"
              >
                {{ formatDatumKurz(gruppe.datum) }}
                <span class="weekday-label">{{ formatWochentag(gruppe.datum) }}</span>
              </th>
            </tr>
            <tr class="time-header-row">
              <th
                v-for="zp in alleZeitpunkte"
                :key="'t-' + zp.key"
                class="time-header"
                :class="{ 'weekend-header': zp.istWochenende, 'day-change-border': zp.istTageswechsel }"
              >
                {{ zp.uhrzeit }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="zeile in sortierteZeilen"
              :key="zeile.produktname"
              :class="{ 'row-quartal': zeile.laufzeit === 'Quartal', 'row-base': zeile.produkttyp === 'Base', 'row-peak': zeile.produkttyp === 'Peak' }"
            >
              <td
                class="product-cell sticky-col"
                :class="{ 'product-base': zeile.produkttyp === 'Base', 'product-peak': zeile.produkttyp === 'Peak' }"
                :title="zeile.produktname"
              >
                <strong>{{ zeile.produktname }}</strong>
                <span class="product-tag" v-if="zeile.laufzeit === 'Quartal'">Q</span>
              </td>
              <td
                v-for="zp in alleZeitpunkte"
                :key="zp.key"
                class="price-cell"
                :class="{ 'day-change-border': zp.istTageswechsel, 'settlement-cell': zeile.preisMap[zp.key] && zeile.preisMap[zp.key].quelle === 'S' }"
                :style="ansichtsModus === 'preis' ? getZellenStyle(zeile, zp.key) : getDeltaZellenStyle(zeile, zp.key)"
                :title="getTooltip(zeile, zp)"
              >
                <template v-if="zeile.preisMap[zp.key]">
                  <!-- Preisansicht -->
                  <div class="cell-content" v-if="ansichtsModus === 'preis'">
                    <div class="price-value">
                      {{ formatPreis(zeile.preisMap[zp.key].price) }}
                      <span v-if="zeile.preisMap[zp.key].quelle === 'S'" class="quelle-badge">S</span>
                    </div>
                    <div class="trend-indicator" v-if="zeile.preisMap[zp.key].abweichung !== null">
                      <v-icon
                        x-small
                        :color="getTrendInfo(zeile, zp.key).textFarbe"
                      >
                        {{ getTrendInfo(zeile, zp.key).pfeil }}
                      </v-icon>
                      <span
                        class="deviation-text"
                        :style="{ color: getTrendInfo(zeile, zp.key).textFarbe }"
                      >
                        {{ formatAbweichung(zeile.preisMap[zp.key].abweichung) }}
                      </span>
                    </div>
                  </div>
                  <!-- Delta-Ansicht -->
                  <div class="cell-content" v-else>
                    <div class="delta-value">
                      {{ formatDelta(zeile.preisMap[zp.key].importDelta) }}
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="missing-cell" :class="{ 'expected-missing': zp.istWochenende }">
                    <v-icon x-small color="grey">{{ zp.istWochenende ? 'mdi-calendar-remove' : 'mdi-alert' }}</v-icon>
                  </div>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>

    <!-- Leer-Zustand: Lade -->
    <v-card outlined v-else-if="$store.state.preismonitor.loading">
      <v-card-text class="text-center py-8">
        <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
        <div class="grey--text">Produktliste wird geladen...</div>
      </v-card-text>
    </v-card>

    <!-- Leer-Zustand: Keine Daten -->
    <v-card outlined v-else-if="produktListe.length === 0 && !$store.state.preismonitor.loading">
      <v-card-text class="text-center py-8">
        <v-icon large color="grey">mdi-database-off</v-icon>
        <div class="mt-2 grey--text">Keine Preisdaten für die gewählten Filter gefunden.</div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      showConfig: false,
      ansichtsModus: "preis",
      schnellfilter: {
        energietyp: "alle",
        produkttyp: "alle",
      },
    }
  },
  computed: {
    filter() {
      return this.$store.state.preismonitor.filter
    },
    produktListe() {
      return this.$store.state.preismonitor.produktListe
    },
    isSubscribed() {
      return this.$store.state.preismonitor.listeners.length > 0
    },
    geladeneProdukte() {
      return this.$store.getters["preismonitor/geladeneProdukte"].length
    },
    fehlendeProdukte() {
      return this.produktListe.length - this.geladeneProdukte
    },
    ladeVorgangAktiv() {
      return this.fehlendeProdukte > 0
    },
    schwellwerte() {
      return this.$store.state.preismonitor.schwellwerte
    },
    farbkonfiguration() {
      return this.$store.state.preismonitor.farbkonfiguration
    },
    deltaKonfiguration() {
      return this.$store.state.preismonitor.deltaKonfiguration
    },
    zeigeQuartale() {
      return this.$store.state.preismonitor.filter.laufzeiten.includes("Q")
    },
    heatmapData() {
      return this.$store.getters["preismonitor/getHeatmapData"]
    },
    alleZeitpunkte() {
      return this.$store.getters["preismonitor/getAlleZeitpunkte"]
    },
    tagesGruppen() {
      return this.$store.getters["preismonitor/getTagesGruppen"]
    },
    gefilterteZeilen() {
      let zeilen = this.heatmapData
      const sf = this.schnellfilter
      if (sf.energietyp !== "alle") {
        zeilen = zeilen.filter((z) => z.energietyp === sf.energietyp)
      }
      if (sf.produkttyp !== "alle") {
        zeilen = zeilen.filter((z) => z.produkttyp === sf.produkttyp)
      }
      return zeilen
    },
    sortierteZeilen() {
      return [...this.gefilterteZeilen].sort((a, b) => a.produktname.localeCompare(b.produktname))
    },
    letzterImportZeit() {
      let maxTs = 0
      for (const zeile of this.heatmapData) {
        if (zeile.letzterPreis && zeile.letzterPreis.timestamp > maxTs) {
          maxTs = zeile.letzterPreis.timestamp
        }
      }
      if (maxTs === 0) return "—"
      return new Date(maxTs).toLocaleString("de-DE")
    },
  },
  methods: {
    async restart() {
      await this.$store.dispatch("preismonitor/startMonitoring")
    },
    async toggleQuartale() {
      const laufzeiten = this.zeigeQuartale
        ? ["Y"]
        : ["Y", "Q"]
      this.$store.state.preismonitor.filter.laufzeiten = laufzeiten
      await this.restart()
    },
    getTrendInfo(zeile, key) {
      const preis = zeile.preisMap[key]
      if (!preis) {
        return { farbe: this.farbkonfiguration.fehlend, pfeil: "", textFarbe: "#757575" }
      }
      return this.$store.getters["preismonitor/getFarbUndPfeil"](
        preis.abweichung,
        zeile.energietyp,
        zeile.produkttyp
      )
    },
    getZellenStyle(zeile, key) {
      const preis = zeile.preisMap[key]
      if (!preis) {
        const zp = this.alleZeitpunkte.find((z) => z.key === key)
        return {
          backgroundColor: zp && zp.istWochenende
            ? "#EEEEEE"
            : this.farbkonfiguration.fehlend,
        }
      }
      const info = this.getTrendInfo(zeile, key)
      const style = { backgroundColor: info.farbe }
      // Settlement-Zellen dunkler darstellen
      if (preis.quelle === "S") {
        style.filter = "brightness(0.82)"
        style.borderBottom = "2px solid #455A64"
      }
      return style
    },
    getTooltip(zeile, zp) {
      const preis = zeile.preisMap[zp.key]
      if (!preis) return zeile.produktname + " — " + zp.datum + " " + zp.uhrzeit + " — Kein Preis"
      let text = zeile.produktname + "\n"
      text += zp.datum + " " + zp.uhrzeit + "\n"
      text += "Preis: " + this.formatPreis(preis.price) + " €/MWh"
      if (preis.quelle) {
        text += "\nQuelle: " + (preis.quelle === "S" ? "Schlusskurs" : "Live")
      }
      if (preis.abweichung !== null) {
        text += "\nAbweichung: " + this.formatAbweichung(preis.abweichung)
      }
      if (preis.importDelta !== null) {
        text += "\nImport-Delta: " + this.formatDelta(preis.importDelta)
      }
      if (preis.created) {
        const d = new Date(preis.created)
        text += "\nDB-Eingang: " + 
          d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) + " " + 
          d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
      }
      return text
    },
    formatDatumKurz(datum) {
      const d = new Date(datum + "T00:00:00")
      return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })
    },
    formatWochentag(datum) {
      const d = new Date(datum + "T00:00:00")
      return d.toLocaleDateString("de-DE", { weekday: "short" })
    },
    formatPreis(price) {
      if (price === null || price === undefined || isNaN(price)) return "—"
      return price.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
    formatAbweichung(val) {
      if (val === null || val === undefined) return ""
      const prefix = val > 0 ? "+" : ""
      return prefix + val.toFixed(2) + "%"
    },
    updateSchwellwert(energietyp, produkttyp, idx, val) {
      const stufen = [...this.schwellwerte[energietyp][produkttyp].stufen]
      stufen[idx] = parseFloat(val) || 0
      this.$store.commit("preismonitor/SET_SCHWELLWERTE", { energietyp, produkttyp, stufen })
    },
    updateFarbe(richtung, idx, val) {
      const farben = [...this.farbkonfiguration[richtung]]
      farben[idx] = val
      this.$store.commit("preismonitor/SET_FARBEN", { richtung, farben })
    },
    formatDelta(deltaMs) {
      if (deltaMs === null || deltaMs === undefined) return "—"
      const totalSek = Math.round(Math.abs(deltaMs) / 1000)
      if (totalSek < 60) return totalSek + "s"
      const min = Math.floor(totalSek / 60)
      if (min < 60) return min + "m"
      const std = Math.floor(min / 60)
      const restMin = min % 60
      if (std < 24) return std + "h" + (restMin > 0 ? restMin + "m" : "")
      const tage = Math.floor(std / 24)
      return tage + "d" + (std % 24 > 0 ? (std % 24) + "h" : "")
    },
    getDeltaZellenStyle(zeile, key) {
      const preis = zeile.preisMap[key]
      if (!preis || preis.importDelta === null) {
        const zp = this.alleZeitpunkte.find((z) => z.key === key)
        return {
          backgroundColor: zp && zp.istWochenende ? "#EEEEEE" : "#E0E0E0",
        }
      }
      const minuten = Math.abs(preis.importDelta) / 60000
      const stufen = this.deltaKonfiguration.stufen
      let farbe = this.deltaKonfiguration.fallback
      for (let i = 0; i < stufen.length; i++) {
        if (minuten < stufen[i].bisMinuten) {
          farbe = stufen[i].farbe
          break
        }
      }
      return { backgroundColor: farbe }
    },
    updateDeltaStufe(idx, feld, val) {
      if (feld === "bisMinuten") {
        this.$store.commit("preismonitor/SET_DELTA_CONFIG", {
          index: idx, bisMinuten: parseFloat(val) || 0,
        })
      } else {
        this.$store.commit("preismonitor/SET_DELTA_CONFIG", {
          index: idx, farbe: val,
        })
      }
    },
    updateDeltaFallback(val) {
      this.$store.commit("preismonitor/SET_DELTA_FALLBACK", val)
    },
  },
  async mounted() {
    if (!this.$store.state.auth.isSignedIn) {
      this.$router.push("/login")
      return
    }
    // Sofort mit dem Monitoring starten
    await this.restart()
  },
  beforeDestroy() {
    // Alle Firestore-Listener stoppen wenn die Seite verlassen wird
    this.$store.dispatch("preismonitor/stopListeners")
  },
}
</script>

<style scoped>
.heatmap-wrapper {
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 340px);
}

.heatmap-table {
  border-collapse: collapse;
  font-size: 11px;
  min-width: 100%;
}

.heatmap-table th,
.heatmap-table td {
  border: 1px solid #e0e0e0;
  padding: 1px 3px;
  white-space: nowrap;
  text-align: center;
}

.product-header {
  background: #37474F;
  color: white;
  position: sticky;
  top: 0;
  z-index: 3;
  min-width: 120px;
}

.day-group-header {
  background: #37474F;
  color: white;
  position: sticky;
  top: 0;
  z-index: 2;
  font-size: 11px;
  border-bottom: none;
  padding: 2px 4px;
}

.day-group-header .weekday-label {
  font-size: 9px;
  opacity: 0.75;
  margin-left: 4px;
}

.time-header {
  background: #546E7A;
  color: white;
  position: sticky;
  top: 26px;
  z-index: 2;
  font-size: 9px;
  min-width: 62px;
  padding: 1px 2px;
}

.weekend-header {
  background: #78909C !important;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
  min-width: 130px;
  text-align: left !important;
}

.sticky-col2 {
  position: sticky;
  left: 130px;
  background: white;
  z-index: 1;
  min-width: 55px;
}

thead .sticky-col {
  z-index: 4;
  background: #37474F;
}

thead .sticky-col2 {
  z-index: 4;
  background: #37474F;
  color: white;
}

.product-cell {
  font-weight: 600;
  font-size: 10px;
  border-right: 2px solid #bdbdbd !important;
}

/* Base: leicht bläulicher Hintergrund */
.product-base {
  background-color: #E3F2FD !important;
}

/* Peak: leicht gelblicher Hintergrund */
.product-peak {
  background-color: #FFF8E1 !important;
}

/* Quartal-Zeilen dezent absetzen */
.row-quartal td {
  border-top: 1px dashed #90A4AE;
}
.row-quartal .product-cell {
  font-style: italic;
}
.row-quartal .product-base {
  background-color: #E0F0FA !important;
}
.row-quartal .product-peak {
  background-color: #FFF3D0 !important;
}

.product-tag {
  font-size: 8px;
  font-weight: 700;
  color: #546E7A;
  margin-left: 3px;
  vertical-align: super;
}

.type-cell {
  border-right: 2px solid #bdbdbd !important;
}

.price-cell {
  cursor: default;
  transition: filter 0.15s;
  width: 62px;
  min-width: 62px;
  max-width: 62px;
  height: 36px;
  max-height: 36px;
  padding: 0 1px !important;
  overflow: hidden;
}

.price-cell:hover {
  filter: brightness(0.88);
  outline: 2px solid #1976D2;
  outline-offset: -2px;
}

.cell-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  line-height: 1.15;
  height: 100%;
  overflow: hidden;
}

.delta-value {
  font-weight: 600;
  font-size: 10px;
  color: #37474F;
}

.price-value {
  font-weight: 600;
  font-size: 10px;
}

.trend-indicator {
  display: flex;
  align-items: center;
  gap: 1px;
  font-size: 9px;
}

.deviation-text {
  font-size: 8px;
  font-weight: 700;
}

.missing-cell {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 32px;
}

.expected-missing {
  opacity: 0.35;
}

.day-change-border {
  border-left: 3px solid #263238 !important;
}

.quelle-badge {
  font-size: 7px;
  font-weight: 700;
  color: #1565C0;
  vertical-align: super;
  margin-left: 1px;
}

.settlement-cell {
  background-color: #E0E0E0 !important;
  filter: brightness(0.88);
}

.settlement-cell .price-value {
  font-weight: 700;
}

tr:hover .product-cell {
  filter: brightness(0.92);
}
</style>
